import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiLoader, FiMail, FiCheckCircle, FiEye, FiEyeOff, FiCheck, FiX, FiArrowLeft, FiArrowRight, FiShield, FiZap, FiTrendingUp } from 'react-icons/fi';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import LinkedInLoginButton from '../../components/auth/LinkedInLoginButton';
import { toast } from 'react-toastify';
import { authApi, promoApi } from '../../services/api';

const BENEFITS = [
  { icon: FiZap, title: 'AI Resume Analysis', desc: 'Get instant feedback on how to improve your resume for any role' },
  { icon: FiShield, title: 'ATS Optimization', desc: 'Ensure your resume passes automated screening systems' },
  { icon: FiTrendingUp, title: 'Career Growth', desc: 'Track your progress and land interviews faster' },
];

const Register = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const promoFlow = queryParams.get('from') === 'promo';
  const promoLeadIdFromUrl = queryParams.get('lead_id');
  const promoEmailFromUrl = queryParams.get('email');

  const prefilledEmail = location.state?.email || (promoFlow ? (promoEmailFromUrl || '') : '');
  const prefilledName = location.state?.name || '';
  const isGoogleRegistration = location.state?.fromGoogle === true;
  const isLinkedInRegistration = location.state?.fromLinkedIn === true;

  const mentorCodeFromUrl = queryParams.get('mentor_code');
  
  const [formData, setFormData] = useState({
    full_name: prefilledName,
    email: prefilledEmail,
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [agreeTerms, setAgreeTerms] = useState(false);
  
  const { register, googleLogin, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [verificationCode, setVerificationCode] = useState('');
  const [mentorCode, setMentorCode] = useState(mentorCodeFromUrl || '');
  
  const navigate = useNavigate();
  
  useEffect(() => {
    // If the user reached /register from the promo /free flow, remember the
    // lead id so that after login (or after Google/LinkedIn auto-login) we
    // can claim it and route them to their full diagnosis report.
    if (promoFlow) {
      try {
        if (promoLeadIdFromUrl) {
          localStorage.setItem('pendingPromoLeadId', promoLeadIdFromUrl);
        }
        localStorage.setItem('redirectAfterLogin', '/resume-diagnosis/report');
      } catch (e) { /* localStorage unavailable */ }
    }
  }, [promoFlow, promoLeadIdFromUrl]);

  useEffect(() => {
    if (isAuthenticated) {
      // For Google / LinkedIn flows the user is already authenticated when
      // they land here. If we have a pending promo lead, claim it and route
      // to the diagnosis report; otherwise fall back to the dashboard.
      const leadId = (() => {
        try { return localStorage.getItem('pendingPromoLeadId'); }
        catch (e) { return null; }
      })();
      if (leadId) {
        promoApi.claim(leadId)
          .catch(() => {})
          .finally(() => {
            try {
              localStorage.removeItem('pendingPromoLeadId');
              localStorage.removeItem('redirectAfterLogin');
            } catch (e) { /* ignore */ }
            navigate('/resume-diagnosis/report');
          });
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (location.state?.email || location.state?.name) {
      setFormData(prev => ({
        ...prev,
        email: location.state.email || prev.email,
        full_name: location.state.name || prev.full_name,
      }));
    }
  }, [location.state?.email, location.state?.name]);

  useEffect(() => {
    const { password } = formData;
    let strength = 0;
    
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    
    setPasswordStrength(strength);
  }, [formData.password]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const getStrengthLabel = () => {
    switch (passwordStrength) {
      case 0: return "Very Weak";
      case 1: return "Weak";
      case 2: return "Fair";
      case 3: return "Good";
      case 4: return "Strong";
      default: return "";
    }
  };
  
  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 0: return "bg-red-500";
      case 1: return "bg-orange-500";
      case 2: return "bg-yellow-500";
      case 3: return "bg-blue-500";
      case 4: return "bg-green-500";
      default: return "bg-gray-200";
    }
  };

  const getPasswordRequirements = () => {
    const { password } = formData;
    return [
      { text: "8+ characters", met: password.length >= 8 },
      { text: "Uppercase", met: /[A-Z]/.test(password) },
      { text: "Number", met: /[0-9]/.test(password) },
      { text: "Special char", met: /[^A-Za-z0-9]/.test(password) }
    ];
  };

  const handleSubmitStep1 = async (e) => {
    e.preventDefault();
    
    const { full_name, email, password, confirmPassword } = formData;
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (!agreeTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      if (isGoogleRegistration) {
        await authApi.registerWithGoogle({
          full_name,
          email,
          password,
          google_verified: true
        });
        
        toast.success('Account created successfully!');
        navigate('/login');
        return;
      } else if (isLinkedInRegistration) {
        await authApi.registerWithLinkedin({
          full_name,
          email,
          password,
          linkedin_verified: true
        });
        
        toast.success('Account created successfully!');
        navigate('/login');
        return;
      } 
      
      await authApi.registerRequest({
        full_name,
        email,
        password
      });
      
      toast.success('Verification code sent to your email');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create an account. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmitStep2 = async (e) => {
    e.preventDefault();
    
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }
    
    try {
      setError('');
      setLoading(true);
      
      await authApi.verifyRegistration(
        formData.email,
        verificationCode,
        mentorCode || null
      );

      toast.success('Account created successfully!');
      if (promoFlow) {
        // Keep the promo context so /login can claim the lead and redirect
        // to the diagnosis report after successful authentication.
        const params = new URLSearchParams({ from: 'promo' });
        if (promoLeadIdFromUrl) params.set('lead_id', promoLeadIdFromUrl);
        if (formData.email) params.set('email', formData.email);
        navigate(`/login?${params.toString()}`, {
          state: { email: formData.email },
        });
      } else {
        navigate('/login');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    try {
      await authApi.resendVerificationCode(formData.email);
      toast.info('A new verification code has been sent to your email');
    } catch (err) {
      console.error('Error resending code:', err);
    }
  };
  
  const currentStep = (isGoogleRegistration || isLinkedInRegistration) ? 1 : step;
  
  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-[48%] relative overflow-hidden bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary-600/20 blur-3xl animate-float" />
          <div className="absolute top-1/2 -right-32 w-80 h-80 rounded-full bg-primary-500/15 blur-3xl animate-float-delayed" />
          <div className="absolute -bottom-20 left-1/4 w-72 h-72 rounded-full bg-primary-400/10 blur-3xl animate-float-slow" />
          <div className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '32px 32px',
            }}
          />
        </div>

        <div className="relative z-10 flex flex-col justify-start items-center gap-y-10 p-12 pt-10 xl:p-16 xl:pt-12 w-full">
          {/* Logo */}
          <div className="self-start">
            <Link to="/" className="flex items-center space-x-3 group">
              <img src="/logo-white.svg" alt="Resumemo" className="h-9 w-auto" />
              <span className="text-xl font-semibold text-white tracking-tight">Resumemo</span>
            </Link>
          </div>

          {/* Main content */}
          <div className="max-w-md w-full text-center">
            <h2 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-4">
              Start your journey to a better career
            </h2>
            <p className="text-primary-200 text-base leading-relaxed mb-10">
              Create your free account and get access to powerful AI tools that help you stand out from the competition.
            </p>
            
            {/* Benefits */}
            <div className="space-y-5">
              {BENEFITS.map((benefit, i) => (
                <div key={i} className="flex items-start space-x-4 text-left">
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
                    <benefit.icon className="w-5 h-5 text-primary-200" />
                  </div>
                  <div>
                    <div className="text-white font-medium text-sm mb-0.5">{benefit.title}</div>
                    <div className="text-primary-300 text-sm leading-relaxed">{benefit.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 max-w-md w-full">
            <p className="text-primary-100 text-sm leading-relaxed italic mb-3">
              "Resumemo helped me tailor my resume for each application. I went from no callbacks to 3 interviews in a week!"
            </p>
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-primary-500/30 flex items-center justify-center text-white text-xs font-semibold">
                SK
              </div>
              <div>
                <div className="text-white text-xs font-medium">Sarah K.</div>
                <div className="text-primary-300 text-xs">Software Engineer</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile header */}
        <header className="lg:hidden w-full py-4 px-5 border-b border-gray-100">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center space-x-2 group">
              <img src="/logo.svg" alt="Resumemo" className="h-7 w-auto" />
              <span className="text-lg font-semibold text-gray-900 tracking-tight">Resumemo</span>
            </Link>
            <Link to="/login" className="text-base text-primary-600 font-medium hover:text-primary-700 transition-colors">
              Sign In
            </Link>
          </div>
        </header>

        {/* Form area */}
        <div className="flex-1 flex items-start justify-center pt-16 xl:pt-24 pb-8 px-5 sm:px-8 lg:px-12 xl:px-20">
          <div className="w-full max-w-[420px]">
            {currentStep === 1 ? (
              <>
                {/* Title */}
                <div className="mb-8">
                  <h1 className="text-[28px] sm:text-[32px] font-bold text-gray-900 tracking-tight mb-2">Create your account</h1>
                  <p className="text-base text-gray-500">
                    {(isGoogleRegistration || isLinkedInRegistration) 
                      ? 'Complete your profile to get started'
                      : 'Free forever. No credit card required.'}
                  </p>
                </div>

                {/* Sign up with Google / LinkedIn */}
                {!isGoogleRegistration && !isLinkedInRegistration && (
                  <>
                    <p className="text-base text-gray-600 mb-3">Sign up with</p>
                    <div className="flex gap-3 mb-6">
                      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                        <div className="flex-1 min-w-0">
                          <GoogleLogin
                            onSuccess={async (credentialResponse) => {
                              try {
                                setLoading(true);
                                const result = await googleLogin(credentialResponse.credential);
                                if (result.success) {
                                  navigate(result.redirectUrl || '/');
                                } else if (result.needsRegistration && result.email) {
                                  navigate('/register', { replace: true, state: { email: result.email, name: result.name || '', fromGoogle: true } });
                                  toast.info('Please complete your registration');
                                }
                              } catch (err) {
                                setError('Failed to sign up with Google. Please try again.');
                                console.error(err);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            onError={() => setError('Google sign up failed. Please try again.')}
                            theme="outline"
                            text="signup_with"
                            size="large"
                            width="100%"
                            style={{ width: '100%' }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <LinkedInLoginButton iconOnly />
                        </div>
                      </GoogleOAuthProvider>
                    </div>
                    <div className="relative mb-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="px-3 bg-white text-sm text-gray-500 uppercase tracking-wider">or continue with email</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Form */}
                <form onSubmit={handleSubmitStep1} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-base animate-shake">
                      {error}
                    </div>
                  )}
                  
                  {/* Full Name */}
                  <div>
                    <label htmlFor="full-name" className="block text-base font-medium text-gray-700 mb-1.5">
                      Full name
                    </label>
                    <input
                      id="full-name"
                      name="full_name"
                      type="text"
                      autoComplete="name"
                      required
                      className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={handleChange}
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label htmlFor="email-address" className="block text-base font-medium text-gray-700 mb-1.5">
                      Email address
                    </label>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  
                  {/* Password */}
                  <div>
                    <label htmlFor="password" className="block text-base font-medium text-gray-700 mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        className="w-full h-12 px-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                        placeholder="Create a password"
                        value={formData.password}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                      </button>
                    </div>
                    
                    {/* Password strength */}
                    {formData.password && (
                      <div className="mt-3 space-y-2">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4].map((level) => (
                            <div
                              key={level}
                              className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                                passwordStrength >= level ? getStrengthColor() : 'bg-gray-100'
                              }`}
                            />
                          ))}
                        </div>
                        <div className="flex items-center justify-between">
                          <span className={`text-sm font-medium ${
                            passwordStrength <= 1 ? 'text-red-500' : 
                            passwordStrength === 2 ? 'text-yellow-500' : 
                            passwordStrength >= 3 ? 'text-green-500' : 'text-gray-400'
                          }`}>
                            {getStrengthLabel()}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          {getPasswordRequirements().map((req, index) => (
                            <div key={index} className={`flex items-center space-x-1.5 text-sm ${req.met ? 'text-green-600' : 'text-gray-400'}`}>
                              {req.met ? <FiCheck className="h-3 w-3 flex-shrink-0" /> : <FiX className="h-3 w-3 flex-shrink-0" />}
                              <span>{req.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Confirm Password */}
                  <div>
                    <label htmlFor="confirm-password" className="block text-base font-medium text-gray-700 mb-1.5">
                      Confirm password
                    </label>
                    <div className="relative">
                      <input
                        id="confirm-password"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        className="w-full h-12 px-4 pr-12 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                        placeholder="Confirm your password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {/* Terms */}
                  <div className="flex items-start pt-1">
                    <input
                      id="agree-terms"
                      name="agree-terms"
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-0.5"
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      required
                    />
                    <label htmlFor="agree-terms" className="ml-2.5 text-base text-gray-500 leading-5">
                      I agree to the{' '}
                      <Link to="/terms" className="text-primary-600 hover:text-primary-700 transition-colors font-medium">
                        Terms
                      </Link>{' '}
                      and{' '}
                      <Link to="/privacy-policy" className="text-primary-600 hover:text-primary-700 transition-colors font-medium">
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  
                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full h-12 flex justify-center items-center rounded-xl text-base font-semibold text-white bg-primary-600 hover:bg-primary-700 active:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    {loading ? (
                      <>
                        <FiLoader className="animate-spin mr-2 h-4 w-4" />
                        {(isGoogleRegistration || isLinkedInRegistration) ? 'Completing...' : 'Creating account...'}
                      </>
                    ) : (
                      (isGoogleRegistration || isLinkedInRegistration) ? 'Complete Registration' : 'Create Account'
                    )}
                  </button>
                </form>
                
                <p className="mt-6 text-center text-base text-gray-500">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    Sign in
                  </Link>
                </p>
              </>
            ) : (
              <>
                {/* Verification Step */}
                <div className="mb-8">
                  <div className="flex justify-center mb-5">
                    <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center">
                      <FiMail className="h-7 w-7 text-emerald-600" />
                    </div>
                  </div>
                  <h1 className="text-[28px] sm:text-[32px] font-bold text-gray-900 tracking-tight mb-2 text-center">Check your email</h1>
                  <p className="text-base text-gray-500 text-center">
                    We've sent a 6-digit code to <span className="text-gray-700 font-medium">{formData.email}</span>
                  </p>
                </div>

                <div className="bg-blue-50/80 border border-blue-100 rounded-xl px-4 py-3 mb-6">
                  <div className="flex items-start">
                    <FiMail className="h-4 w-4 text-blue-500 mt-0.5 mr-2.5 flex-shrink-0" />
                    <p className="text-base text-blue-700">
                      Check your inbox (and spam folder) for the verification code.
                    </p>
                  </div>
                </div>
                
                <form onSubmit={handleSubmitStep2} className="space-y-4">
                  {error && (
                    <div className="bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-base animate-shake">
                      {error}
                    </div>
                  )}
                  
                  <div>
                    <label htmlFor="verification-code" className="block text-base font-medium text-gray-700 mb-1.5">
                      Verification Code
                    </label>
                    <input
                      id="verification-code"
                      name="verification-code"
                      type="text"
                      maxLength="6"
                      required
                      className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-center text-lg tracking-[0.3em] font-mono"
                      placeholder="000000"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="mentor-code" className="block text-base font-medium text-gray-700 mb-1.5">
                      Mentor Code <span className="text-gray-400 text-sm font-normal">(Optional)</span>
                    </label>
                    <input
                      id="mentor-code"
                      name="mentor-code"
                      type="text"
                      className="w-full h-12 px-4 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200"
                      placeholder="Enter mentor code if you have one"
                      value={mentorCode}
                      onChange={(e) => setMentorCode(e.target.value)}
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    className="w-full h-12 flex justify-center items-center rounded-xl text-base font-semibold text-white bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    {loading ? (
                      <>
                        <FiLoader className="animate-spin mr-2 h-4 w-4" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FiCheckCircle className="mr-2 h-4 w-4" />
                        Verify & Create Account
                      </>
                    )}
                  </button>
                </form>
                
                <div className="mt-6 pt-5 border-t border-gray-100 text-center space-y-3">
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-base text-primary-600 hover:text-primary-700 transition-colors font-medium"
                  >
                    Didn't receive the code? Resend
                  </button>
                  
                  <div>
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="text-base text-gray-500 hover:text-gray-700 transition-colors flex items-center mx-auto"
                    >
                      <FiArrowLeft className="mr-1 w-3.5 h-3.5" />
                      Back to registration
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="py-5 px-5 sm:px-8 lg:px-10 border-t border-gray-100">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
            <p className="text-sm text-gray-500">&copy; 2026 Resumemo. All rights reserved.</p>
            <div className="flex space-x-5">
              <Link to="/privacy-policy" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Register;
